import React, { useState } from 'react';
import {
  LayoutDashboard,
  Box,
  Layers,
  ShoppingCart,
  CreditCard,
  RotateCcw,
  Users,
  Star,
  Archive,
  Percent,
  BarChart3,
  FileText,
  Bell,
  Image,
  Truck,
  Store,
  ShieldCheck,
  MessageCircle,
  Settings,
  Clock5,
  LogOut,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';

const menuItems = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    href: '/admin/dashboard',
  },
  {
    title: 'Products',
    icon: Box,
    submenu: [
      { title: 'Products', href: '/admin/products' },
      { title: 'Add Product', href: '/admin/products/add' },
      { title: 'Categories', href: '/admin/categories' },
    ],
  },
  {
    title: 'Orders',
    icon: ShoppingCart,
    href: '/admin/orders',
  },
  {
    title: 'Payment History',
    icon: CreditCard,
    href: '/admin/payments',
  },
  {
    title: 'Refunds',
    icon: RotateCcw,
    href: '/admin/refunds',
  },
  {
    title: 'Customers',
    icon: Users,
    href: '/admin/customers',
  },
  {
    title: 'Reviews',
    icon: Star,
    href: '/admin/reviews',
  },
  {
    title: 'Inventory',
    icon: Archive,
    href: '/admin/inventory',
  },
  {
    title: 'Coupons',
    icon: Percent,
    href: '/admin/coupons',
  },
  {
    title: 'Analytics',
    icon: BarChart3,
    href: '/admin/analytics',
  },
  {
    title: 'Reports',
    icon: FileText,
    href: '/admin/reports',
  },
  {
    title: 'Notifications',
    icon: Bell,
    href: '/admin/notifications',
  },
  {
    title: 'Banner Management',
    icon: Image,
    href: '/admin/banners',
  },
  {
    title: 'CMS Management',
    icon: Layers,
    href: '/admin/cms',
  },
  {
    title: 'Delivery Management',
    icon: Truck,
    href: '/admin/delivery',
  },
  {
    title: 'Stores',
    icon: Store,
    href: '/admin/stores',
  },
  {
    title: 'Roles & Permissions',
    icon: ShieldCheck,
    href: '/admin/roles',
  },
  {
    title: 'AI Support Chat',
    icon: MessageCircle,
    href: '/admin/ai-support',
  },
  {
    title: 'Settings',
    icon: Settings,
    href: '/admin/settings',
  },
  {
    title: 'Activity Logs',
    icon: Clock5,
    href: '/admin/logs',
  },
  {
    title: 'Logout',
    icon: LogOut,
    onClick: 'logout',
  },
];

const Sidebar = ({ isOpen, setIsOpen, collapsed = false }) => {
  const [expandedMenu, setExpandedMenu] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAdmin();

  const isActivePath = (href) => {
    if (!href) return false;
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
    setIsOpen(false);
  };

  const initials = (user?.name || 'Admin User')
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const toggleSubmenu = (title) => {
    setExpandedMenu((prev) => (prev === title ? null : title));
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

        <aside
        className={`fixed inset-y-0 left-0 z-50 flex h-screen flex-col overflow-hidden border-r border-[#3e2f20] bg-[#0c0907] shadow-2xl transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 ${collapsed ? 'w-[90px]' : 'w-[280px]'}`}
      >
        <div className="flex h-full flex-col overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-5 py-6 border-b border-[#3e2f20]">
            <div className={`flex items-center gap-3 transition-opacity ${collapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
              <div className="grid h-12 w-12 place-items-center rounded-3xl bg-[#8b5e34] text-xl font-semibold text-[#f4e9db] shadow-[0_20px_40px_rgba(0,0,0,0.12)]">
                F
              </div>
              <div>
                <p className="text-sm font-semibold text-[#f4e9db]">Fosen Furniture</p>
                <p className="text-xs uppercase tracking-[0.18em] text-[#b8a38a]">Admin Dashboard</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 border-b border-[#3e2f20] px-5 py-5 text-[#f5e9da]">
            <div className="h-14 w-14 flex-shrink-0 rounded-full bg-[#a97752] grid place-items-center text-xl font-semibold uppercase shadow-inner shadow-black/20">
              {initials}
            </div>
            {!collapsed && (
              <div className="truncate">
                <p className="font-semibold">{user?.name || 'Admin User'}</p>
                <p className="text-sm text-[#b39b81]">{user?.role || 'Admin'}</p>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-px py-4">
            <nav className="space-y-1 px-2">
              {menuItems.map((item) => {
                const active = item.href ? isActivePath(item.href) : Array.isArray(item.submenu) ? item.submenu.some((sub) => isActivePath(sub.href)) : false;
                const hasSubmenu = Array.isArray(item.submenu);
                const expanded = expandedMenu === item.title;
                return (
                  <div key={item.title} className="relative">
                    {hasSubmenu ? (
                      <button
                        type="button"
                        onClick={() => toggleSubmenu(item.title)}
                        onMouseEnter={() => collapsed && setExpandedMenu(item.title)}
                        onMouseLeave={() => collapsed && setExpandedMenu(null)}
                        className={`group flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-sm font-medium transition ${active ? 'bg-[#3d291d] text-[#f6e8d6]' : 'text-[#d8c3aa] hover:bg-[#221a14] hover:text-[#f8efe5]' } ${collapsed ? 'justify-center' : ''}`}
                        title={collapsed ? item.title : ''}
                      >
                        <item.icon size={20} className="text-[#d8c3aa]" />
                        {!collapsed && <span className="flex-1 text-left">{item.title}</span>}
                        {!collapsed && <ChevronDown size={16} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />}
                      </button>
                    ) : item.onClick === 'logout' ? (
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-sm font-medium text-[#d8c3aa] transition hover:bg-[#221a14] hover:text-[#f8efe5]"
                      >
                        <item.icon size={20} />
                        {!collapsed && <span className="flex-1 text-left">{item.title}</span>}
                      </button>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={() => setIsOpen(false)}
                        className={`group flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-sm font-medium transition ${active ? 'bg-[#3d291d] text-[#f6e8d6]' : 'text-[#d8c3aa] hover:bg-[#221a14] hover:text-[#f8efe5]' } ${collapsed ? 'justify-center' : ''}`}
                        title={collapsed ? item.title : ''}
                      >
                        <item.icon size={20} />
                        {!collapsed && <span className="flex-1 text-left">{item.title}</span>}
                      </Link>
                    )}

                    {hasSubmenu && expanded && !collapsed && (
                      <div className="mt-2 space-y-1 rounded-2xl bg-[#120e0b] px-3 py-2">
                        {item.submenu.map((subitem) => (
                          <Link
                            key={subitem.href}
                            to={subitem.href}
                            onClick={() => {
                              setIsOpen(false);
                              setExpandedMenu(null);
                            }}
                            className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm transition ${isActivePath(subitem.href) ? 'bg-[#2e2218] text-[#f0dfc7]' : 'text-[#cbb79f] hover:bg-[#231b15] hover:text-[#f8efe5]'}`}
                          >
                            <ChevronRight size={16} />
                            {subitem.title}
                          </Link>
                        ))}
                      </div>
                    )}

                    {hasSubmenu && collapsed && (
                      <AnimatePresence>
                        {expanded && (
                          <motion.div
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            className="absolute left-full top-0 z-50 w-52 rounded-3xl border border-[#3e2f20] bg-[#120e0b] p-3 shadow-2xl"
                          >
                            {item.submenu.map((subitem) => (
                              <Link
                                key={subitem.href}
                                to={subitem.href}
                                onClick={() => {
                                  setIsOpen(false);
                                  setExpandedMenu(null);
                                }}
                                className={`block rounded-2xl px-3 py-2 text-sm ${isActivePath(subitem.href) ? 'bg-[#2e2218] text-[#f7e8d1]' : 'text-[#cbb79f] hover:bg-[#231b15] hover:text-[#f8efe5]'}`}
                              >
                                {subitem.title}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
